import { ref, computed } from '@vue/composition-api'
import store from '@/store'
import i18n from '@/libs/i18n'

export default function useOperationalTimeFleet() {
  // Refs
  const refOperationalTimeTable = ref(null)
  
  // Table Handlers
  const tableColumns = ref([
    { key: 'plate', label: i18n.t('Identifier'), sortable: false, stickyColumn: true, thStyle: { minWidth: '200px', position: 'sticky', left: 0, zIndex: 2, backgroundColor: '#fff' } },
    { key: 'meanActualRunTime', label: i18n.t('Mean Actual Run Time'), sortable: false, thStyle: { minWidth: '150px' } },
    { key: 'meanEfficiencyPercentage', label: i18n.t('Mean Efficiency Percentage'), sortable: false, thStyle: { minWidth: '150px' } }
  ])
  const perPage = ref(10)
  const totalItems = ref(0)
  const currentPage = ref(1)
  const perPageOptions = ref([10, 25, 50, 100])
  const sortBy = ref('plate')
  const sortDesc = ref(false)
  
  // Filter Refs
  const shiftStartTime = ref('07:00')
  const shiftEndTime = ref('21:00')
  const breaks = ref('02:00')
  const startDate = ref('')
  const endDate = ref('')
  const showFilters = ref(true)
  const isLoading = ref(false)
  
  // Additional Filter Refs
  const searchQuery = ref('')
  const searchOwner = ref('')
  const ownerId = ref('')
  const assetTypeId = ref('')
  const assetBrandId = ref('')
  const assetModelId = ref('')
  const assetGroupId = ref('')
  
  // Data
  const operationalData = ref([])
  const hasSearched = ref(false)

  const dataMeta = computed(() => {
    const localItemsCount = operationalData.value.length
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalItems.value,
    }
  })

  // Inicializar datas padrão (hoje - 7 dias até hoje)
  const initializeDefaultDates = () => {
    const today = new Date()
    const sevenDaysAgo = new Date()
    // Subtrair 6 dias para ter exatamente 7 dias (incluindo hoje)
    sevenDaysAgo.setDate(today.getDate() - 6)
    
    endDate.value = formatDateToInput(today)
    startDate.value = formatDateToInput(sevenDaysAgo)
  }

  const formatDateToInput = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Validar período máximo de 7 dias para busca
  const validateDateRangeForSearch = () => {
    if (!startDate.value || !endDate.value) {
      return { valid: false, message: i18n.t('Start and end dates are required') }
    }
    
    const start = new Date(startDate.value)
    const end = new Date(endDate.value)
    
    if (start > end) {
      return { valid: false, message: i18n.t('Start date must be before end date') }
    }
    
    const diffTime = Math.abs(end - start)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1 // +1 para incluir ambos os dias
    
    if (diffDays > 7) {
      return { valid: false, message: i18n.t('Maximum query period is 7 days') }
    }
    
    return { valid: true }
  }

  // Validar período máximo de 31 dias para exportação
  const validateDateRangeForExport = () => {
    if (!startDate.value || !endDate.value) {
      return { valid: false, message: i18n.t('Start and end dates are required') }
    }
    
    const start = new Date(startDate.value)
    const end = new Date(endDate.value)
    
    if (start > end) {
      return { valid: false, message: i18n.t('Start date must be before end date') }
    }
    
    const diffTime = Math.abs(end - start)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1 // +1 para incluir ambos os dias
    
    if (diffDays > 31) {
      return { valid: false, message: i18n.t('Maximum export period is 31 days') }
    }
    
    return { valid: true }
  }

  // Função de validação genérica (mantida para compatibilidade)
  const validateDateRange = () => {
    return validateDateRangeForSearch()
  }

  // Calcular dias do período selecionado
  const getSelectedDateRangeDays = () => {
    if (!startDate.value || !endDate.value) return 0
    
    const start = new Date(startDate.value)
    const end = new Date(endDate.value)
    
    // Normalizar as datas para meia-noite para cálculo preciso
    start.setHours(0, 0, 0, 0)
    end.setHours(0, 0, 0, 0)
    
    const diffTime = Math.abs(end - start)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    // Retornar +1 para incluir ambos os dias (início e fim)
    return diffDays + 1
  }

  // Gerar colunas dinamicamente baseado nos dados
  const generateTableColumns = (data) => {
    // Sempre retornar pelo menos as colunas básicas
    const columns = [
      { key: 'plate', label: i18n.t('Identifier'), sortable: false, stickyColumn: true, thStyle: { minWidth: '200px', position: 'sticky', left: 0, zIndex: 2, backgroundColor: '#fff' } },
    ]
    
    // Adicionar colunas para cada data no período
    if (data && data.length > 0 && data[0] && data[0].operationalTime) {
      data[0].operationalTime.forEach((item) => {
        columns.push({
          key: `date_${item.date}`,
          label: item.date,
          sortable: false,
          thStyle: { minWidth: '220px' }
        })
      })
    }
    
    // Adicionar colunas de média
    columns.push(
      { key: 'meanActualRunTime', label: i18n.t('Mean Actual Run Time'), sortable: false, thStyle: { minWidth: '150px' } },
      { key: 'meanEfficiencyPercentage', label: i18n.t('Mean Efficiency Percentage'), sortable: false, thStyle: { minWidth: '150px' } }
    )
    
    return columns
  }

  const fetchOperationalTime = () => {
    const validation = validateDateRange()
    
    if (!validation.valid) {
      return Promise.reject(new Error(validation.message))
    }

    isLoading.value = true

    return store
      .dispatch('app-operation-time/fetchSearchOperationalTime', {
        search: searchQuery.value,
        page: currentPage.value,
        perPage: perPage.value,
        ownerId: ownerId.value,
        assetTypeId: assetTypeId.value,
        assetBrandId: assetBrandId.value,
        assetModelId: assetModelId.value,
        assetGroupId: assetGroupId.value,
        shiftStartTime: shiftStartTime.value,
        shiftEndTime: shiftEndTime.value,
        breaks: breaks.value,
        start: startDate.value,
        end: endDate.value,
      })
      .then(response => {
        const { data, length, totalItems: total } = response.data
        
        operationalData.value = data
        totalItems.value = total
        hasSearched.value = true
        
        // Gerar colunas dinamicamente
        const newColumns = generateTableColumns(data)
        tableColumns.value = newColumns
        
        isLoading.value = false
        return data
      })
      .catch(error => {
        isLoading.value = false
        throw error
      })
  }

  const refetchData = () => {
    return fetchOperationalTime()
  }

  const clearFilters = () => {
    initializeDefaultDates()
    shiftStartTime.value = '07:00'
    shiftEndTime.value = '21:00'
    breaks.value = '02:00'
    searchQuery.value = ''
    searchOwner.value = ''
    ownerId.value = ''
    assetTypeId.value = ''
    assetBrandId.value = ''
    assetModelId.value = ''
    assetGroupId.value = ''
    currentPage.value = 1
    hasSearched.value = false
    operationalData.value = []
    totalItems.value = 0
  }

  const hasActiveFilters = computed(() => {
    const today = new Date()
    const sevenDaysAgo = new Date()
    sevenDaysAgo.setDate(today.getDate() - 7)
    
    const defaultStart = formatDateToInput(sevenDaysAgo)
    const defaultEnd = formatDateToInput(today)
    
    return shiftStartTime.value !== '07:00' ||
           shiftEndTime.value !== '21:00' ||
           breaks.value !== '02:00' ||
           startDate.value !== defaultStart ||
           endDate.value !== defaultEnd ||
           searchQuery.value !== '' ||
           searchOwner.value !== '' ||
           ownerId.value !== '' ||
           assetTypeId.value !== '' ||
           assetBrandId.value !== '' ||
           assetModelId.value !== '' ||
           assetGroupId.value !== ''
  })
  
  // Update methods for owner filter
  const updateSearchOwner = (value) => {
    searchOwner.value = value
  }
  
  const updateOwnerId = (value) => {
    ownerId.value = value
  }

  // Formatar tempo para exibição (apenas HH:mm)
  const formatTime = (timeString) => {
    if (!timeString || timeString === '00:00:00') return '00:00'
    
    // Remove os milissegundos se existirem
    let time = timeString.split('.')[0]
    
    // Pega apenas HH:mm (remove os segundos)
    const timeParts = time.split(':')
    if (timeParts.length >= 2) {
      return `${timeParts[0]}:${timeParts[1]}`
    }
    
    return time
  }

  // Formatar percentual
  const formatPercentage = (value) => {
    return value ? `${value.toFixed(2)}%` : '0.00%'
  }

  // Obter variante do badge baseado na eficiência
  const getEfficiencyVariant = (percentage) => {
    if (percentage >= 90) return 'success'
    if (percentage >= 70) return 'info'
    if (percentage >= 50) return 'warning'
    if (percentage > 0) return 'danger'
    return 'secondary'
  }

  // Obter dados operacionais de uma data específica
  const getOperationalTimeForDate = (item, dateLabel) => {
    if (!item || !item.operationalTime) return null
    return item.operationalTime.find(ot => ot.date === dateLabel)
  }

  // Inicializar datas ao criar o composable
  initializeDefaultDates()

  return {
    fetchOperationalTime,
    refOperationalTimeTable,
    tableColumns,
    perPage,
    currentPage,
    totalItems,
    dataMeta,
    perPageOptions,
    sortBy,
    sortDesc,
    
    shiftStartTime,
    shiftEndTime,
    breaks,
    startDate,
    endDate,
    showFilters,
    isLoading,
    operationalData,
    
    searchQuery,
    searchOwner,
    ownerId,
    assetTypeId,
    assetBrandId,
    assetModelId,
    assetGroupId,
    
    refetchData,
    clearFilters,
    hasActiveFilters,
    validateDateRange,
    validateDateRangeForSearch,
    validateDateRangeForExport,
    getSelectedDateRangeDays,
    formatTime,
    formatPercentage,
    getEfficiencyVariant,
    updateSearchOwner,
    updateOwnerId,
    getOperationalTimeForDate,
    hasSearched
  }
}

